import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../api";

/**
 * The load / edit / dirty / save cycle every Settings section repeats.
 *
 * Dirtiness is a structural comparison against the **last saved** snapshot, not
 * a "something was typed" flag: an admin who edits a field and then types the
 * original value back has nothing to save, and a save bar that keeps insisting
 * otherwise trains people to ignore it.
 *
 * `saved` stays true briefly after a successful save so the shell's bar can
 * hold on the confirmation rather than vanishing the instant the state updates.
 */
export function useSectionForm({ path, submitPath, transform, onSaved }) {
  const [saved0, setSaved0] = useState(null);   // last known server state
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const adopt = useCallback((record) => {
    setSaved0(record);
    setForm(record);
  }, []);

  /**
   * Accept a server record as the new baseline while keeping unsaved edits to
   * every field except `keys`.
   *
   * Needed because asset uploads write immediately and return the whole
   * record: adopting it wholesale would silently discard an admin's half-typed
   * org name, and ignoring it would leave the uploaded logo looking unsaved.
   */
  const adoptFields = useCallback((record, keys) => {
    setSaved0(record);
    setForm((prev) => {
      if (!prev) return record;
      const next = { ...prev };
      for (const key of keys) next[key] = record[key];
      return next;
    });
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    api.get(path)
      .then((r) => adopt(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [path, adopt]);

  useEffect(() => { reload(); }, [reload]);

  const patch = useCallback((key) => (valueOrEvent) => {
    const value = valueOrEvent?.target !== undefined
      ? valueOrEvent.target.value
      : valueOrEvent;
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError("");
    try {
      const body = transform ? transform(form) : form;
      const r = await api.put(submitPath || path, body);
      adopt(r.data);
      onSaved?.(r.data);
      setSaved(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setSaved(false), 2400);
    } catch (e) {
      console.error(`Saving ${path} failed:`, e);
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }, [form, transform, submitPath, path, adopt, onSaved]);

  const discard = useCallback(() => {
    setForm(saved0);
    setError("");
  }, [saved0]);

  const dirty = Boolean(form) && Boolean(saved0)
    && JSON.stringify(form) !== JSON.stringify(saved0);

  return {
    form, setForm, patch, adopt, adoptFields, reload,
    dirty, loading, saving, saved,
    error, setError, save, discard,
  };
}
